/**
 * Connection management — modems + wifi for the fleet.
 *
 * Modems pulled from `device.modems`. WiFi state is fetched on demand
 * via wifi.scan / wifi.list_profiles cloud commands; results land via
 * the SSE command-update stream and are cached in local state.
 *
 * Buttons enqueue commands through the standard fleet command path
 * (props.onCommand). Status/result feedback comes from the global
 * CommandDrawer.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Radio,
  Power,
  RefreshCw,
  RotateCcw,
  Rocket,
  Router,
  Cable,
  RefreshCcw,
  SlidersHorizontal,
  Wifi,
  WifiOff,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import api from '../services/api';
import { FleetCommand, FleetDevice, FleetEthernet, FleetEvent, FleetModem } from '../types';

interface ConnectionPanelProps {
  device: FleetDevice;
  onCommand: (kind: string, payload?: Record<string, any>) => Promise<void>;
}

interface WifiNetwork {
  ssid: string;
  signal_pct: number | null;
  security: string;
  freq_mhz: number | null;
  bssid: string;
  in_use: boolean;
}

interface WifiProfile {
  name: string;
  uuid: string;
  ssid?: string;
  type?: string;
}

function rsrpColorClass(rsrp: number | null | undefined): string {
  if (rsrp == null) return 'text-fg-muted';
  if (rsrp >= -95) return 'text-status-online';
  if (rsrp >= -110) return 'text-status-warning';
  return 'text-status-error';
}

function signalColorClass(pct: number | null | undefined): string {
  if (pct == null) return 'text-fg-muted';
  if (pct >= 70) return 'text-status-online';
  if (pct >= 40) return 'text-status-warning';
  return 'text-status-error';
}

function signalBorderClass(pct: number | null | undefined): string {
  if (pct == null) return 'border-fg-muted';
  if (pct >= 70) return 'border-status-online';
  if (pct >= 40) return 'border-status-warning';
  return 'border-status-error';
}

const EthernetCard: React.FC<{
  iface: FleetEthernet;
  online: boolean;
  onAction: (kind: string, payload?: Record<string, any>) => Promise<void>;
}> = ({ iface, online, onAction }) => {
  const [busy, setBusy] = useState(false);
  const [probeResult, setProbeResult] = useState<{
    detected: boolean;
    ping_ok: boolean;
    tcp_grpc_ok: boolean;
  } | null>(null);

  useEffect(() => {
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (
        evt.type === 'command_update' &&
        evt.command.kind === 'eth.starlink_probe' &&
        evt.command.status === 'done' &&
        evt.command.result &&
        evt.command.payload?.interface === iface.name
      ) {
        const r = evt.command.result as any;
        setProbeResult({
          detected: !!r.detected,
          ping_ok: !!r.ping_ok,
          tcp_grpc_ok: !!r.tcp_grpc_ok,
        });
      }
    });
    return () => {
      try {
        unsub?.();
      } catch {
        // ignore
      }
    };
  }, [iface.name]);

  const carrier = iface.carrier_ok;
  const stateClass = carrier
    ? 'border-status-online text-status-online'
    : 'border-fg-muted text-fg-muted';
  const isStarlink = iface.is_starlink === true;

  const fire = async (kind: string, extra: Record<string, any> = {}) => {
    setBusy(true);
    try {
      await onAction(kind, { interface: iface.name, ...extra });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-border bg-bg-surface">
      <CardContent className="p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Cable className="size-4 text-status-buffered" />
          <span className="font-mono text-sm">{iface.name}</span>
          <Badge variant="outline" className={cn('font-mono', stateClass)}>
            {iface.operstate || 'unknown'}
          </Badge>
          {iface.is_default_route && (
            <Badge variant="outline" className="border-status-buffered font-mono text-status-buffered">
              default route
            </Badge>
          )}
          {isStarlink && (
            <Badge
              variant="outline"
              className="flex items-center gap-1 border-accent-purple font-mono text-accent-purple"
            >
              <Rocket className="size-3" />
              Starlink
            </Badge>
          )}
        </div>

        <div className="mb-2 flex flex-wrap gap-3 font-mono text-xs">
          <span className="text-fg">
            {iface.ip || 'no ip'} {iface.gateway ? `→ gw ${iface.gateway}` : ''}
          </span>
          <span className="text-fg">mtu {iface.mtu ?? '—'}</span>
          <span className="text-fg-muted">{iface.mac}</span>
        </div>

        {probeResult && (
          <div
            className={cn(
              'mb-2 rounded border p-2 font-mono text-xs',
              probeResult.detected
                ? 'border-accent-purple text-accent-purple'
                : 'border-status-warning text-fg-secondary',
            )}
          >
            dishy 192.168.100.1 — ping {probeResult.ping_ok ? 'ok' : 'fail'} · grpc:9200{' '}
            {probeResult.tcp_grpc_ok ? 'ok' : 'fail'} ·{' '}
            {probeResult.detected ? 'Starlink detected' : 'no Starlink hardware'}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!online || busy}
                    onClick={() => fire('eth.starlink_probe')}
                    className="border-accent-purple text-accent-purple hover:bg-accent-purple/10"
                  >
                    <Rocket />
                    Probe Starlink
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Ping dishy at 192.168.100.1 + check gRPC port</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant={isStarlink ? 'default' : 'outline'}
                    disabled={!online || busy}
                    onClick={() => fire('eth.starlink_mark', { is_starlink: !isStarlink })}
                    className={
                      isStarlink
                        ? 'bg-accent-purple text-bg-base hover:bg-accent-purple/80'
                        : 'border-accent-purple text-accent-purple hover:bg-accent-purple/10'
                    }
                  >
                    {isStarlink ? 'Is Starlink' : 'Mark as Starlink'}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {isStarlink ? 'Unmark this interface as Starlink' : 'Mark this interface as Starlink'}
              </TooltipContent>
            </Tooltip>
            {isStarlink && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!online || busy}
                      onClick={() => fire('eth.starlink_optimize', { enable: !iface.starlink_optimized })}
                      className={cn(
                        iface.starlink_optimized
                          ? 'border-status-warning text-status-warning'
                          : 'border-status-online text-status-online',
                      )}
                    >
                      <SlidersHorizontal />
                      {iface.starlink_optimized ? 'Revert tuning' : 'Apply tuning'}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {iface.starlink_optimized
                    ? 'Revert MTU to 1500'
                    : 'Set MTU 1480 + enable BBR congestion control'}
                </TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
};

const ModemCard: React.FC<{
  modem: FleetModem;
  online: boolean;
  onAction: (kind: string, payload?: Record<string, any>) => Promise<void>;
}> = ({ modem, online, onAction }) => {
  const r = modem.recovery;
  const portId = modem.port_id;
  const targetPayload = portId == null ? {} : { port_id: portId };
  return (
    <Card className="border-border bg-bg-surface">
      <CardContent className="p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Radio className="size-4 text-status-buffered" />
          <span className="font-mono text-sm">
            port{portId} · {modem.model || '—'} · {modem.interface || '—'}
          </span>
          <Badge
            variant="outline"
            className={cn(
              'font-mono',
              modem.state === 'connected'
                ? 'border-status-online text-status-online'
                : 'border-status-warning text-status-warning',
            )}
          >
            {modem.state || 'unknown'}
          </Badge>
        </div>

        <div className="mb-2 flex flex-wrap gap-3 font-mono text-xs text-fg">
          <span>
            {modem.operator || '—'} {modem.tech || ''} {modem.band || ''}
          </span>
          <span>
            {modem.ip || 'no ip'} · APN {modem.apn || '—'}
          </span>
          <span>reg {modem.registration || '—'}</span>
        </div>

        <div className="mb-2 flex flex-wrap gap-3 font-mono text-xs">
          <span className={rsrpColorClass(modem.rsrp_dbm)}>
            RSRP {modem.rsrp_dbm ?? '—'} dBm
          </span>
          <span className="text-fg">RSRQ {modem.rsrq_db ?? '—'} dB</span>
          <span className="text-fg">SINR {modem.sinr_db?.toFixed?.(1) ?? '—'} dB</span>
          <span className="text-fg">RSSI {modem.rssi_dbm ?? '—'} dBm</span>
          <span className={signalColorClass(modem.signal_quality_pct)}>
            qual {modem.signal_quality_pct ?? '—'}%
          </span>
        </div>

        <div className="mb-2 font-mono text-xs text-fg-muted">
          cell {modem.cell_id || '—'} TAC {modem.tac || '—'} IMEI {modem.imei || '—'}
        </div>

        {r && (
          <div className="mb-2 rounded border border-status-warning p-2 font-mono text-xs text-status-warning">
            recovery: {r.fault} · step {r.step_idx} attempt {r.attempts_at_step}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <TooltipProvider>
            {([
              {
                kind: 'modem.snapshot_rf',
                label: 'Snapshot',
                tip: 'Fresh RF snapshot (mmcli + AT+QENG)',
                icon: <RefreshCcw />,
                cls: 'border-status-buffered text-status-buffered',
              },
              {
                kind: 'modem.reset',
                label: 'Reset',
                tip: 'MM-friendly reset (mmcli --reset)',
                icon: <RotateCcw />,
                cls: 'border-status-warning text-status-warning',
              },
              {
                kind: 'modem.rf_reset',
                label: 'RF cycle',
                tip: 'Disable + re-enable (RF cycle, no firmware reset)',
                icon: <RefreshCw />,
                cls: 'border-status-warning text-status-warning',
              },
              {
                kind: 'modem.power_cycle',
                label: 'Power cycle',
                tip: 'Hard power cycle (AT+QPOWD=1, ~25s downtime)',
                icon: <Power />,
                cls: 'border-status-error text-status-error',
              },
              {
                kind: 'modem.gnss_enable',
                label: 'GNSS on',
                tip: 'Enable Quectel GNSS engine (AT+QGPS=1, autostart on boot)',
                icon: null,
                cls: 'border-accent-purple text-accent-purple',
              },
              {
                kind: 'modem.gnss_disable',
                label: 'GNSS off',
                tip: 'Disable Quectel GNSS engine (AT+QGPSEND)',
                icon: null,
                cls: 'border-fg-muted text-fg-muted',
              },
              {
                kind: 'modem.gnss_status',
                label: 'GNSS ?',
                tip: 'Read Quectel GNSS engine + last fix',
                icon: null,
                cls: 'border-border text-fg-muted',
              },
            ] as const).map((b) => (
              <Tooltip key={b.kind}>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!online || portId == null}
                      onClick={() => onAction(b.kind, targetPayload)}
                      className={cn(b.cls, 'hover:bg-bg-elevated')}
                    >
                      {b.icon}
                      {b.label}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{b.tip}</TooltipContent>
              </Tooltip>
            ))}
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
};

const WifiBlock: React.FC<{
  device: FleetDevice;
  onAction: (kind: string, payload?: Record<string, any>) => Promise<void>;
}> = ({ device, onAction }) => {
  const [networks, setNetworks] = useState<WifiNetwork[]>([]);
  const [profiles, setProfiles] = useState<WifiProfile[]>([]);
  const [busy, setBusy] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [pendingSsid, setPendingSsid] = useState<string>('');
  const [pendingPsk, setPendingPsk] = useState<string>('');
  const [pendingNetworkSecure, setPendingNetworkSecure] = useState<boolean>(true);

  const ingestCommand = useCallback((cmd: FleetCommand) => {
    if (cmd.status !== 'done' || !cmd.result) return;
    if (cmd.kind === 'wifi.scan') {
      const list = (cmd.result.networks as WifiNetwork[]) || [];
      setNetworks(list);
    } else if (cmd.kind === 'wifi.list_profiles') {
      const list = (cmd.result.profiles as WifiProfile[]) || [];
      setProfiles(list);
    }
  }, []);

  useEffect(() => {
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (evt.type === 'command_update' && evt.command.device_id === device.device_id) {
        ingestCommand(evt.command);
      }
    });
    return () => {
      try {
        unsub?.();
      } catch {
        // ignore
      }
    };
  }, [device.device_id, ingestCommand]);

  const fetchedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!device.online) return;
    if (fetchedForRef.current === device.device_id) return;
    fetchedForRef.current = device.device_id;
    onAction('wifi.list_profiles').catch(() => undefined);
    // intentional: ignore onAction churn; we re-fire only on device change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.device_id, device.online]);

  const fireScan = async () => {
    if (!device.online) return;
    setBusy(true);
    try {
      await onAction('wifi.scan', { rescan: true });
    } finally {
      setBusy(false);
    }
  };

  const startConnectFlow = (network: WifiNetwork) => {
    const knownProfile = profiles.find((p) => p.ssid === network.ssid);
    const isOpen = network.security === '' || network.security === 'OPEN';
    if (knownProfile || isOpen) {
      setPendingSsid(network.ssid);
      setPendingPsk('');
      setPendingNetworkSecure(false);
      doConnect(network.ssid, undefined);
    } else {
      setPendingSsid(network.ssid);
      setPendingPsk('');
      setPendingNetworkSecure(true);
      setConnectDialogOpen(true);
    }
  };

  const doConnect = async (ssid: string, psk: string | undefined) => {
    setBusy(true);
    try {
      await onAction('wifi.connect', psk ? { ssid, psk } : { ssid });
      setConnectDialogOpen(false);
      setPendingPsk('');
      setTimeout(() => onAction('wifi.list_profiles').catch(() => undefined), 4000);
    } finally {
      setBusy(false);
    }
  };

  const fireDisconnect = async () => {
    if (!device.online) return;
    setBusy(true);
    try {
      await onAction('wifi.disconnect', {});
    } finally {
      setBusy(false);
    }
  };

  const liveConnections = device.wifi?.connections || [];
  const liveSsids = useMemo(
    () => new Set(liveConnections.map((c) => c.ssid).filter(Boolean) as string[]),
    [liveConnections],
  );
  const inUse = liveConnections[0] || null;
  const apActive = !!device.wifi?.ap_active;
  const apSsid = device.wifi?.ap_ssid || 'Yelve-AP';

  const toggleAp = async () => {
    setBusy(true);
    try {
      await onAction(apActive ? 'wifi.ap_stop' : 'wifi.ap_start', {});
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-border bg-bg-surface">
      <CardContent className="p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Wifi className="size-4 text-status-online" />
          <span className="flex-1 font-mono text-sm">
            WiFi ·{' '}
            {inUse
              ? `connected to ${inUse.ssid} (${inUse.signal_dbm ?? '—'} dBm on ${inUse.device})`
              : 'not connected'}
            {apActive && ` · AP "${apSsid}" up`}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant={apActive ? 'default' : 'outline'}
                    disabled={!device.online || busy}
                    onClick={toggleAp}
                    className={
                      apActive
                        ? 'bg-accent-purple text-bg-base hover:bg-accent-purple/80'
                        : 'border-accent-purple text-accent-purple hover:bg-accent-purple/10'
                    }
                  >
                    <Router />
                    {apActive ? 'AP on' : 'AP'}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{apActive ? `Stop AP "${apSsid}"` : `Start AP "${apSsid}"`}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={!device.online || busy}
                    onClick={fireScan}
                    className="text-status-buffered"
                    aria-label="Rescan + refresh"
                  >
                    <RefreshCw />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Rescan + refresh</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={!device.online || busy || !inUse}
                    onClick={fireDisconnect}
                    className="text-status-error"
                    aria-label="Disconnect"
                  >
                    <WifiOff />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Disconnect</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {profiles.length > 0 && (
          <>
            <div className="text-xs text-fg-secondary">Saved profiles:</div>
            <div className="mt-1 mb-2 flex flex-wrap gap-2">
              {profiles.map((p) => (
                <button
                  key={p.uuid}
                  onClick={() => doConnect(p.ssid || p.name, undefined)}
                  className="rounded-full border border-border bg-bg-elevated px-3 py-0.5 font-mono text-xs hover:border-accent"
                >
                  {p.ssid || p.name}
                </button>
              ))}
            </div>
            <div className="my-2 h-px bg-border" />
          </>
        )}

        <div className="text-xs text-fg-secondary">
          Visible networks{networks.length === 0 ? ' (none yet — hit refresh)' : ''}:
        </div>
        <div className="mt-1 max-h-60 space-y-1 overflow-auto">
          {networks.map((n, idx) => {
            const live = liveSsids.has(n.ssid);
            return (
              <div
                key={`${n.bssid}-${idx}`}
                className={cn(
                  'flex items-center gap-2 rounded border px-2 py-1',
                  live
                    ? 'border-status-online bg-status-online/10'
                    : 'border-border bg-bg-elevated',
                )}
              >
                <span className="flex-1 font-mono text-sm">{n.ssid}</span>
                <span className="font-mono text-xs text-fg-muted">
                  {n.security || 'OPEN'} · {n.freq_mhz} MHz
                </span>
                <Badge
                  variant="outline"
                  className={cn(
                    'min-w-[60px] justify-center font-mono',
                    signalBorderClass(n.signal_pct),
                    signalColorClass(n.signal_pct),
                  )}
                >
                  {n.signal_pct ?? '—'}%
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!device.online || busy || live}
                  onClick={() => startConnectFlow(n)}
                  className="border-status-online text-status-online hover:bg-status-online/10"
                >
                  {live ? 'Connected' : 'Connect'}
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>

      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-accent">Connect to {pendingSsid}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-fg-secondary">
            {pendingNetworkSecure ? 'Enter the network password.' : 'Open network — no password.'}
          </p>
          {pendingNetworkSecure && (
            <Input
              autoFocus
              type="password"
              placeholder="Wi-Fi password"
              value={pendingPsk}
              onChange={(e) => setPendingPsk(e.target.value)}
              className="mt-2"
            />
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConnectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={pendingNetworkSecure && pendingPsk.length < 8}
              onClick={() => doConnect(pendingSsid, pendingNetworkSecure ? pendingPsk : undefined)}
              className="bg-status-online text-bg-base hover:bg-status-online/80"
            >
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export const ConnectionPanel: React.FC<ConnectionPanelProps> = ({ device, onCommand }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-accent">Connections</h3>
      {(device.ethernet || []).map((e) => (
        <EthernetCard key={`eth-${e.name}`} iface={e} online={device.online} onAction={onCommand} />
      ))}
      {(device.modems || []).length === 0 ? (
        <p className="text-xs text-fg-muted">no modems detected on this device</p>
      ) : (
        (device.modems || []).map((m) => (
          <ModemCard
            key={`${m.port_id ?? '?'}-${m.mm_id ?? '?'}`}
            modem={m}
            online={device.online}
            onAction={onCommand}
          />
        ))
      )}
      <WifiBlock device={device} onAction={onCommand} />
    </div>
  );
};

export default ConnectionPanel;
